import { API_URL } from '../helpers/constants';
import getTurnstileToken, { TurnstileTiming } from './turnstile';

const STORAGE_KEY = 'tttSessionToken';
const OFFSET_KEY = 'tttClockOffset';

type SessionTokenResponse = {
  token?: string;
  expiresIn?: number;
  serverTime?: number;
};

/**
 * The API endpoints driving search and booking expect a session token minted by us. It is not a
 * credential and identifies nobody: it exists so the API can count a visitor's activity against a
 * single key, which an address alone cannot provide once a scraper rotates through proxies.
 */
let pending: Promise<string | null> | null = null;

/**
 * What the interface needs to know about the session: whether a search may be waiting on a
 * challenge, and how many sessions arrived late - a search refused for want of one can then start
 * again on its own.
 */
export type SessionStatus = {
  isMinting: boolean;
  lateSessions: number;
};

const listeners = new Set<() => void>();

let lateSessions = 0;

const notify = (): void => {
  listeners.forEach((listener) => listener());
};

export const getSessionStatus = (): SessionStatus => ({
  isMinting: pending !== null,
  lateSessions,
});

/**
 * @description Calls `listener` whenever the session status changes.
 * @param {Function} listener
 * @returns {Function} Unsubscribes
 */
export const subscribeToSessionStatus = (
  listener: () => void,
): (() => void) => {
  listeners.add(listener);

  return () => {
    listeners.delete(listener);
  };
};

/**
 * How long a failed mint is remembered. Without it, every protected call re-ran the whole challenge
 * - up to Turnstile's 8 s budget each time - for a visitor whose browser had just failed it, and
 * re-hit the minting quota until the API answered 429. Short enough that a transient failure (a
 * flaky network, a slow challenge) is retried soon; long enough that a persistent one (a filtering
 * proxy, an exhausted quota) stops costing seconds on every call.
 */
const MINT_BACKOFF_MS = 60 * 1000;

let lastFailureAt = 0;

const inBackoff = (): boolean => Date.now() - lastFailureAt < MINT_BACKOFF_MS;

/**
 * @description Lets the next call run a challenge again at once. Only for a retry the visitor asked
 * for: the backoff exists so that nothing else re-runs a challenge that just failed.
 * @returns {void}
 */
export const allowSessionRetry = (): void => {
  lastFailureAt = 0;
};

const read = (): string | null => {
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch (error) {
    // Private browsing and blocked storage both throw here. The token then lives for this page only.
    return null;
  }
};

const write = (token: string): void => {
  try {
    window.localStorage.setItem(STORAGE_KEY, token);
  } catch (error) {
    // Nothing to do: we simply mint a new token on the next page load.
  }
};

/**
 * Signatures carry a timestamp the API checks against its own clock. A device whose clock drifted
 * would otherwise have every request refused, with nothing the visitor could see or fix, so we
 * measure the gap once and sign in the server's time rather than ours.
 */
let clockOffset = Number(
  (() => {
    try {
      return window.sessionStorage.getItem(OFFSET_KEY);
    } catch (error) {
      return 0;
    }
  })(),
);

export const getClockOffset = (): number =>
  Number.isFinite(clockOffset) ? clockOffset : 0;

const rememberClockOffset = (serverTime?: number): void => {
  if (!Number.isFinite(serverTime)) {
    return;
  }

  clockOffset = (serverTime as number) - Math.floor(Date.now() / 1000);

  try {
    window.sessionStorage.setItem(OFFSET_KEY, String(clockOffset));
  } catch (error) {
    // Kept in memory for this page only.
  }
};

/** Sessions the API has handed out on this page: tells a late token whether it is still needed. */
let sessionsIssued = 0;

/** A session obtained from a late token while the mint that started the challenge was still pending. */
let lateSession: string | null = null;

const requestSession = async (
  body: Record<string, unknown>,
): Promise<string | null> => {
  const response = await fetch(`${API_URL}/session/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    return null;
  }

  const { serverTime, token }: SessionTokenResponse = await response.json();

  rememberClockOffset(serverTime);

  if (!token) {
    return null;
  }

  write(token);
  sessionsIssued += 1;

  return token;
};

const timingFields = ({ elapsedMs, visibility }: TurnstileTiming) => ({
  turnstileElapsedMs: elapsedMs,
  turnstileVisibility: visibility,
});

const mint = async (): Promise<string | null> => {
  const issuedBefore = sessionsIssued;

  lateSession = null;

  /**
   * Cloudflare can still produce a token after we stopped waiting and reported a timeout. It is
   * valid for five minutes, so it is turned into a session straight away - unless one was obtained
   * meanwhile - and the backoff is lifted: the visitor's next search goes through instead of being
   * refused for another minute.
   */
  const onLateToken = (turnstileToken: string, timing: TurnstileTiming) => {
    if (sessionsIssued > issuedBefore) {
      return;
    }

    requestSession({
      turnstileToken,
      ...timingFields(timing),
      turnstileLate: true,
    })
      .then((token) => {
        if (token) {
          lateSession = token;
          lastFailureAt = 0;
          lateSessions += 1;
          notify();
        }
      })
      .catch(() => {
        // Nothing to do: the next call mints again once the backoff is over.
      });
  };

  // Solved once per session, not per request: the API only challenges at this one point.
  const {
    error: turnstileError,
    timing,
    token: turnstileToken,
  } = await getTurnstileToken({ onLateToken });

  // Posted even when the challenge produced nothing. The API would refuse it, but that refusal is
  // the only way it learns a browser failed Cloudflare - skipping it would erase exactly the
  // blocks the "session.blocked.cloudflare" metric exists to count. The error says why, so the
  // metric can tell a suspected bot from an ad blocker, and the timing says whether the challenge
  // was slow or stalled in a hidden tab.
  return requestSession(
    turnstileToken
      ? { turnstileToken, ...timingFields(timing) }
      : { turnstileToken: null, turnstileError, ...timingFields(timing) },
  );
};

/**
 * @description Returns the current session token, minting one on first use.
 * Concurrent callers share a single in-flight request so a page load does not mint several.
 * @param {boolean} [force] Discard the stored token and mint a new one (used after a 401)
 * @returns {Promise<string | null>}
 */
export default async function getSessionToken(
  force = false,
): Promise<string | null> {
  if (!force) {
    const stored = read();

    if (stored) {
      return stored;
    }
  }

  // A mint that just failed would fail again: answer at once rather than re-run the challenge.
  if (inBackoff()) {
    return null;
  }

  if (!pending) {
    // Never rejects: a request that cannot be signed is still worth sending - with the gate
    // switched off it is served, with it on it is refused cleanly - whereas a thrown mint used to
    // abort the request before it was even attempted.
    pending = mint()
      .catch((): null => null)
      .then((token) => {
        // A late token may have been turned into a session while the refusal was on its way back.
        const session = token || lateSession;

        lastFailureAt = session ? 0 : Date.now();

        return session;
      })
      .finally(() => {
        pending = null;
        notify();
      });
    notify();
  }

  return pending;
}

/**
 * @description Starts minting in the background, so the first search does not wait for it.
 *
 * Minting lazily put the whole challenge - loading Turnstile, solving it, calling the API - in
 * front of the first protected request, which is to say in front of the visitor's first search.
 * Started as soon as the app is up - public/index.html has already begun downloading Turnstile -
 * it runs while the visitor is still reading the page and typing, and the token is waiting by the
 * time they submit. Waiting for the browser to be idle used to delay it by up to 3 s, and by 1.5 s
 * on Safari and in the iOS app, which matters on a results page, whose search starts on load.
 * The token is stored for a day, so this costs one challenge per visitor per day, not one per page.
 *
 * @returns {void}
 */
export const warmSessionToken = (): void => {
  setTimeout(() => {
    void getSessionToken();
  }, 0);
};
